import { create } from 'zustand';
import type { ToastActionElement, ToastProps } from '@/components/ui/toast';

export const TOAST_LIMIT = 1;
export const TOAST_REMOVE_DELAY = 1000000;

export type ToastVariant =
  | 'default'
  | 'destructive'
  | 'success'
  | 'error'
  | 'warning'
  | 'info';

export type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  variant?: ToastVariant;
};

export interface ToastStore {
  toasts: ToasterToast[];
  addToast: (toast: Omit<ToasterToast, 'id'> & { id?: string }) => string;
  updateToast: (id: string, toast: Partial<ToasterToast>) => void;
  dismissToast: (id?: string) => void;
  removeToast: (id?: string) => void;
}

let count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export const useToastStore = create<ToastStore>((set, get) => ({
  toasts: [],
  addToast: (toast) => {
    const id = toast.id ?? genId();
    set((state) => ({
      toasts: [{ ...toast, id, open: true }, ...state.toasts].slice(
        0,
        TOAST_LIMIT,
      ),
    }));
    return id;
  },
  updateToast: (id, toast) => {
    set((state) => ({
      toasts: state.toasts.map((t) => (t.id === id ? { ...t, ...toast } : t)),
    }));
  },
  dismissToast: (id) => {
    const ids = id ? [id] : get().toasts.map((t) => t.id);
    ids.forEach((toastId) => {
      if (!toastTimeouts.has(toastId)) {
        const timeout = setTimeout(() => {
          toastTimeouts.delete(toastId);
          get().removeToast(toastId);
        }, TOAST_REMOVE_DELAY);
        toastTimeouts.set(toastId, timeout);
      }
    });
    set((state) => ({
      toasts: state.toasts.map((t) =>
        id === undefined || t.id === id ? { ...t, open: false } : t,
      ),
    }));
  },
  removeToast: (id) => {
    if (id === undefined) {
      set({ toasts: [] });
    } else {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    }
  },
}));

export type Toast = Omit<ToasterToast, 'id'>;

export function toast(props: Toast) {
  const id = useToastStore.getState().addToast({
    ...props,
    onOpenChange: (open) => {
      if (!open) useToastStore.getState().dismissToast(id);
    },
  });
  return {
    id,
    dismiss: () => useToastStore.getState().dismissToast(id),
    update: (newProps: Partial<ToasterToast>) =>
      useToastStore.getState().updateToast(id, newProps),
  };
}

// Helper variants for easier usage
toast.success = (props: Omit<Toast, 'variant'>) =>
  toast({ ...props, variant: 'success' });
toast.error = (props: Omit<Toast, 'variant'>) =>
  toast({ ...props, variant: 'error' });
toast.warning = (props: Omit<Toast, 'variant'>) =>
  toast({ ...props, variant: 'warning' });
toast.info = (props: Omit<Toast, 'variant'>) =>
  toast({ ...props, variant: 'info' });
